import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Badge, Gap, Spacing, Text, Icon } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useUrgencyDiscountUIEnabled from 'hooks/useUrgencyDiscountUIEnabled';
import useShowPricesByGroup from 'hooks/useShowPricesByGroup';
import useMixDiscountFaresByAvailability from 'hooks/useMixDiscountFaresByAvailability';
import usePurchasePricingSeatZones from 'hooks/usePurchasePricingSeatZones';
import usePricingTotal from 'hooks/usePricingTotal';
import {
  getDiscountAvailability,
  getPassengerTotalBySelectionIndex,
} from 'hooks/usePricingBeforeCheckout';
import usePurchase from 'hooks/store/usePurchase';
import useSeatsBus from 'hooks/store/useSeatsBus';
import OverbookingBadge from 'ui/atoms/OverbookingBadge';
import { safeParsePrice } from 'utils/currency';
import { hasOversoldSeats } from 'utils/overbooking';
import { hasLapChildData } from 'utils/lapChild';
import adjacentLogo from 'images/seats/adjacentLogo.svg';
import { DiscountBadge as UrgencyDiscountBadge } from 'components/search/DynamicPricingDiscount';
import PricingRow from '../../ui/atoms/PricingRow';
import discountGFA from '../../images/brands-unique/gfa/early-discount.png';
import discountETN from '../../images/brands-unique/etn/discountETN.png';
import { resolveSeatZoneForDetails } from '../../utils/seatPricing';
import ProductsBreakdown from './ProductsBreakdown';

const earlyDiscountIcons = {
  etn: discountETN,
  costaline: discountETN,
  gho: discountETN,
  gfa: discountGFA,
  default: 'emojiSparkles',
};

/**
 * Gets the custom early discount icon for brands that use one in results.
 * @param {string} brand - Current brand.
 * @returns {string|undefined} Brand-specific early discount icon.
 */
const getEarlyDiscountIcon = (brand) => earlyDiscountIcons[brand];

// These are the possible descriptions returned by the Cruz del Sur WS, its the only tenant using it
const fareDescriptionMap = {
  INSUPERABLE: 'Insuperable',
  OCASION: 'Ocasión',
  VIP: 'VIP',
  LOCURA: 'Locura',
  REGULAR: 'Regular',
};

/**
 * Get fare description
 * @param {string} description - Fare description
 * @param {boolean} showSeatFareDescription - Whether to show fare description
 * @returns {string|null} - Formatted fare description or null if invalid/hidden
 */
const getFareDescription = (description, showSeatFareDescription) => {
  const isValidDescription = description && fareDescriptionMap[description];
  if (!showSeatFareDescription || !isValidDescription) {
    return null;
  }
  return fareDescriptionMap[description];
};

/**
 * Process ticket data for seat-based pricing
 * @param {object} ticket - The ticket item
 * @param {function} t - Translation function
 * @returns {object} - Processed seat-based ticket data
 */
const processSeatBasedTicket = (ticket, t) => {
  const passengerName = '';
  const hasEarlyDiscount = false;
  const showSeat = !!ticket.number;
  const showSeatFloor = !!ticket.seatFloor;
  const seatHasNumber = Number.isFinite(Number(ticket.number));
  const discountLabels = { label: t('label.discount') };

  return {
    ...ticket,
    passengerName,
    hasEarlyDiscount,
    showSeat,
    showSeatFloor,
    seatHasNumber,
    discountLabels,
    amount: ticket.price,
    discountAmount: 0,
    passengerId: ticket.index,
    subtotal: 0,
    taxes: 0,
    total: ticket.price,
    seat: ticket.number,
    isAdjacentSeat: false,
    category: 'seat',
  };
};

/**
 * Process ticket data for regular passenger pricing
 * @param {object} ticket - The ticket item
 * @param {function} t - Translation function
 * @param {boolean} purchasePricingDetailedDiscount - Whether to show detailed discount
 * @returns {object} - Processed passenger ticket data
 */
const processPassengerTicket = (ticket, t, purchasePricingDetailedDiscount) => {
  const passengerName = `${ticket.firstName} ${ticket.lastName || ''}`;
  const hasEarlyDiscount = ticket.discountReason === 'compra anticipada';
  const showSeat = !!ticket.seat;
  const showSeatFloor = !!ticket.seatFloor;
  const seatHasNumber = Number.isFinite(Number(ticket.seat));

  let discountLabels = { label: t('label.discount') };
  if (purchasePricingDetailedDiscount && ticket.discountReason && ticket.discountPercent) {
    discountLabels = {
      label: t('label.savings', {
        reason: ticket.discountReason,
      }),
      discountLabel: `-${ticket.discountPercent}%`,
    };
  } else if (ticket.discountPercent) {
    // Fallback for when feature flag is disabled but we have discount percentage
    discountLabels = {
      ...discountLabels,
      discountLabel: `-${ticket.discountPercent}%`,
    };
  }

  return {
    ...ticket,
    passengerName,
    hasEarlyDiscount,
    showSeat,
    showSeatFloor,
    seatHasNumber,
    discountLabels,
  };
};

/**
 * Process ticket data for display based on ticket type
 * @param {object} ticket - The ticket item
 * @param {boolean} useSeatBased - Whether to use seat-based pricing
 * @param {function} t - Translation function
 * @param {boolean} purchasePricingDetailedDiscount - Whether to show detailed discount
 * @returns {object} - Processed ticket data
 */
const processTicketData = (ticket, useSeatBased, t, purchasePricingDetailedDiscount) => {
  return useSeatBased
    ? processSeatBasedTicket(ticket, t)
    : processPassengerTicket(ticket, t, purchasePricingDetailedDiscount);
};

/**
 * Renders the legacy detailed discount badge.
 * @param {object} props - Component props.
 * @param {string} props.label - Discount percentage label.
 * @returns {JSX.Element|null} Legacy discount badge.
 */
const LegacyDiscountBadge = ({ label }) => {
  if (!label) return null;

  return (
    <Badge roundCorners type="success" smallPadding>
      <Text color="white" elementType="span" size="XS" weight="semibold">
        {label}
      </Text>
    </Badge>
  );
};

LegacyDiscountBadge.propTypes = {
  label: PropTypes.string,
};

/**
 * Renders the passenger discount visual indicator.
 * @param {object} props - Component props.
 * @param {number|string} props.discountAmount - Discount amount.
 * @param {boolean} props.hasEarlyDiscount - Whether the ticket has an early/urgency discount.
 * @param {object} props.discountLabels - Discount labels.
 * @param {string} props.brand - Current brand.
 * @param {boolean} props.showDetailedDiscount - Whether the legacy detailed discount badge is enabled.
 * @param {boolean} props.showDiscountUrgency - Whether the urgency discount UI is enabled.
 * @returns {JSX.Element|null} Discount visual indicator.
 */
const PassengerDiscountIndicator = ({
  discountAmount,
  hasEarlyDiscount,
  discountLabels,
  brand,
  showDetailedDiscount,
  showDiscountUrgency,
}) => {
  if (Number(discountAmount) <= 0) return null;

  const discountLabel = discountLabels?.discountLabel;

  if (showDiscountUrgency) {
    const discountIcon = hasEarlyDiscount ? getEarlyDiscountIcon(brand) : null;
    if (!discountLabel) return discountIcon ? <Icon type={discountIcon} size="S" /> : null;

    return (
      <>
        {discountIcon && <Icon type={discountIcon} size="S" />}
        <UrgencyDiscountBadge percent={discountLabel} />
      </>
    );
  }

  if (hasEarlyDiscount) {
    return <Icon type={getEarlyDiscountIcon(brand) ?? earlyDiscountIcons.default} size="S" />;
  }

  if (!showDetailedDiscount) return null;

  return <LegacyDiscountBadge label={discountLabel} />;
};

PassengerDiscountIndicator.propTypes = {
  discountAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hasEarlyDiscount: PropTypes.bool,
  discountLabels: PropTypes.object,
  brand: PropTypes.string,
  showDetailedDiscount: PropTypes.bool,
  showDiscountUrgency: PropTypes.bool,
};

/**
 * Row component
 * @param {object} props - label, discountLabel, price, hasDiscount, isMain, icon, primary, description, titleSize.
 * @param {string} props.label - Label to show.
 * @param {string} props.discountLabel - Discount label to show.
 * @param {number} props.price - Price to show.
 * @param {boolean} props.hasDiscount - If has discount.
 * @param {boolean} props.isMain - If is main.
 * @param {string} props.icon - Icon to show.
 * @param {boolean} props.primary - If is primary.
 * @param {string} props.description - Description to show.
 * @param {string} props.titleSize - Title size.
 * @returns {JSX.Element} - RowComponent JSX element.
 */
const RowComponent = ({
  label,
  discountLabel,
  price,
  hasDiscount,
  isMain,
  icon,
  primary,
  description,
  titleSize,
}) => {
  return (
    <PricingRow
      label={label}
      discountLabel={discountLabel}
      isMain={isMain}
      hasDiscount={hasDiscount}
      price={price}
      primary={primary}
      icon={icon}
      description={description}
      titleSize={titleSize}
    />
  );
};

RowComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleSize: PropTypes.string,
  discountLabel: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  hasDiscount: PropTypes.bool,
  isMain: PropTypes.bool,
  icon: PropTypes.string,
  primary: PropTypes.bool,
  description: PropTypes.string,
};

/**
 * PassengerTicketHeader component.
 * Display the passenger or seat information header.
 * @param {object} props - passengerName, totalPrice, isOpen, hasCollapsibleTrigger, discountAmount, hasEarlyDiscount, discountLabels, brand, showDiscount, showDiscountUrgency.
 * @param {string} props.passengerName - Passenger name.
 * @param {number|string} props.totalPrice - Total price for the passenger.
 * @param {boolean} props.isOpen - Whether the collapsible section is open.
 * @param {boolean} props.hasCollapsibleTrigger - Whether to show collapsed passenger view.
 * @param {number|string} props.discountAmount - Discount amount.
 * @param {boolean} props.hasEarlyDiscount - Whether has early discount.
 * @param {object} props.discountLabels - Discount labels.
 * @param {string} props.brand - Brand name.
 * @param {boolean} props.showDiscount - Display legacy discount badge.
 * @param {boolean} props.showDiscountUrgency - Display urgency discount badge.
 * @returns {JSX.Element} PassengerTicketHeader component.
 */
const PassengerTicketHeader = ({
  passengerName,
  totalPrice,
  isOpen,
  hasCollapsibleTrigger,
  discountAmount,
  hasEarlyDiscount,
  discountLabels,
  brand,
  showDiscount,
  showDiscountUrgency,
}) => (
  <Spacing size="S" alignItems="center" justifyContent="space-between">
    <Text
      weight={hasCollapsibleTrigger || isOpen ? 'semibold' : 'regular'}
      size="S"
      color={hasCollapsibleTrigger || isOpen ? 'grayStrong' : 'grayMedium'}
    >
      {passengerName}
    </Text>

    <Spacing size="XS" alignItems="center">
      <PassengerDiscountIndicator
        discountAmount={discountAmount}
        hasEarlyDiscount={hasEarlyDiscount}
        discountLabels={discountLabels}
        brand={brand}
        showDetailedDiscount={showDiscount}
        showDiscountUrgency={showDiscountUrgency}
      />

      <LocalizedCurrency
        currencyStyle="currency"
        color={hasCollapsibleTrigger || isOpen ? 'grayStrong' : 'grayMedium'}
        price={safeParsePrice(totalPrice)}
        decimals={2}
        size="S"
        weight={hasCollapsibleTrigger || isOpen ? 'semibold' : 'regular'}
      />
    </Spacing>
  </Spacing>
);

PassengerTicketHeader.propTypes = {
  passengerName: PropTypes.string,
  totalPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isOpen: PropTypes.bool,
  hasCollapsibleTrigger: PropTypes.bool,
  discountAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hasEarlyDiscount: PropTypes.bool,
  discountLabels: PropTypes.object,
  brand: PropTypes.string,
  showDiscount: PropTypes.bool,
  showDiscountUrgency: PropTypes.bool,
};

/**
 * TicketPriceDetails component
 * Displays pricing details for a ticket
 *
 * @param {Object} props - Component props
 * @param {string} props.tripSlug - Trip slug
 * @param {string|number} props.passengerId - Passenger ID
 * @param {string} props.segment - Trip segment.
 * @param {number} props.discountAmount - Discount amount
 * @param {boolean} props.hasEarlyDiscount - Whether has early discount
 * @param {Object} props.discountLabels - Discount labels
 * @param {number} props.subtotal - Subtotal
 * @param {number} props.taxes - Taxes
 * @param {number} props.toll - Per-passenger toll amount
 * @param {number} props.boardingTax - Per-passenger boarding tax amount
 * @param {number} props.managementCosts - Per-passenger management costs amount
 * @param {boolean} props.showBreakdown - Whether to itemize the fare breakdown (toll, boarding tax, management costs)
 * @param {string[]} props.canShowAtZero - Breakdown keys allowed to render when their value is zero
 * @param {boolean} props.customManagementCostsLabel - Whether to use the custom management costs label
 * @param {string} props.brand - Brand name
 * @param {string} props.externalCouponCode - External coupon code
 * @param {boolean} props.showDiscountUrgency - Whether the urgency discount UI is enabled.
 * @param {Function} props.t - Translation function
 * @returns {JSX.Element} - TicketPriceDetails component
 */
const TicketPriceDetails = ({
  tripSlug,
  passengerId,
  segment,
  discountAmount,
  hasEarlyDiscount,
  discountLabels,
  subtotal,
  taxes,
  toll,
  boardingTax,
  managementCosts,
  showBreakdown,
  canShowAtZero,
  customManagementCostsLabel,
  brand,
  externalCouponCode,
  showDiscountUrgency,
  t,
}) => {
  const tollAmount = Number(toll) || 0;
  const boardingTaxAmount = Number(boardingTax) || 0;
  const managementCostsAmount = Number(managementCosts) || 0;

  // When the breakdown is enabled, the subtotal is split into the net fare plus its
  // components (toll, boarding tax, management costs) so the lines add up to the passenger
  // total, mirroring the PurchasePricingTrip layout. Otherwise the fare stays lumped.
  const fareSubtotal = showBreakdown
    ? Number(subtotal) - tollAmount - boardingTaxAmount - managementCostsAmount
    : subtotal;

  const showToll = showBreakdown && (tollAmount > 0 || canShowAtZero.includes('toll'));
  const showBoardingTax =
    showBreakdown && (boardingTaxAmount > 0 || canShowAtZero.includes('boardingTax'));
  const showManagementCosts =
    showBreakdown && (managementCostsAmount > 0 || canShowAtZero.includes('managementCosts'));
  // In breakdown mode the tax line only shows when there are taxes (e.g. empresaunida has none),
  // matching how the other breakdown rows are gated. Outside breakdown mode it always shows.
  const showTaxes = !showBreakdown || Number(taxes) > 0 || canShowAtZero.includes('taxes');
  const earlyDiscountIcon = getEarlyDiscountIcon(brand);
  const legacyEarlyDiscountIcon = earlyDiscountIcon ?? earlyDiscountIcons.default;
  const resolvedEarlyDiscountIcon = showDiscountUrgency
    ? earlyDiscountIcon
    : legacyEarlyDiscountIcon;

  return (
    <>
      <ProductsBreakdown tripSlug={tripSlug} passengerId={passengerId} segment={segment} />

      {discountAmount > 0 &&
        (hasEarlyDiscount ? (
          <RowComponent
            label={t('trips:early_discount')}
            price={discountAmount}
            hasDiscount
            primary
            icon={resolvedEarlyDiscountIcon}
          />
        ) : (
          <RowComponent label={discountLabels.label} price={discountAmount} hasDiscount />
        ))}

      {fareSubtotal > 0 && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-subtotal`}
          label={t('label.subtotal')}
          price={fareSubtotal}
        />
      )}

      {showToll && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-toll`}
          label={t('toll')}
          price={tollAmount}
        />
      )}

      {showBoardingTax && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-boarding-tax`}
          label={t('boardingTax')}
          price={boardingTaxAmount}
        />
      )}

      {showManagementCosts && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-management-costs`}
          label={t(customManagementCostsLabel ? 'commissioningRate' : 'managementCosts')}
          price={managementCostsAmount}
        />
      )}

      {showTaxes && (
        <RowComponent
          key={`${tripSlug}-${passengerId}-tax`}
          label={t('extra_price')}
          price={taxes}
        />
      )}

      {externalCouponCode && (
        <Spacing size="S" alignItems="center">
          <Text color="grayMedium">{t('label.coupon')}:</Text>
          <Badge hasShadow roundCorners type="success" smallPadding>
            <Text color="white" elementType="span" size="S" weight="semibold">
              {externalCouponCode}
            </Text>
          </Badge>
        </Spacing>
      )}
    </>
  );
};

TicketPriceDetails.propTypes = {
  tripSlug: PropTypes.string.isRequired,
  passengerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  segment: PropTypes.string,
  discountAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hasEarlyDiscount: PropTypes.bool,
  discountLabels: PropTypes.object,
  subtotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  taxes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  toll: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  boardingTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  managementCosts: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showBreakdown: PropTypes.bool,
  canShowAtZero: PropTypes.arrayOf(PropTypes.string),
  customManagementCostsLabel: PropTypes.bool,
  brand: PropTypes.string,
  externalCouponCode: PropTypes.string,
  showDiscountUrgency: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

/**
 * SeatBasedDetails component.
 * Renders seat number, deck badge, zone dot, and price for seat-based pricing rows.
 *
 * @param {Object} props - ticketData, t, showSeatFloor, seatFloor, seatZone, seatPrice.
 * @param {Object} props.ticketData - Ticket data object.
 * @param {Function} props.t - Translation function.
 * @param {boolean} props.showSeatFloor - Whether to show seat floor badge.
 * @param {string|number} props.seatFloor - Seat floor label.
 * @param {{ color: string|null, zoneKey: string, label: string }} props.seatZone - Resolved zone for display.
 * @param {number} props.seatPrice - Seat price for the row.
 * @returns {JSX.Element} SeatBasedDetails component.
 */
const SeatBasedDetails = ({ ticketData, t, showSeatFloor, seatFloor, seatZone, seatPrice }) => (
  <Spacing size="XS" vertical fullWidth>
    <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
      <Text size="S" color="grayLight" weight="regular">
        {ticketData.showSeat && ticketData.seatHasNumber
          ? `${t('general:seat')} ${ticketData.seat}`
          : t('text.no_assigned_seat')}
      </Text>
      {showSeatFloor && (
        <Badge roundCorners type="gray">
          <Text elementType="span" size="S" weight="semibold">
            {t('general:deck')}
            <Text elementType="span" size="S" weight="bold">
              {' '}
              {seatFloor}
            </Text>
          </Text>
        </Badge>
      )}
    </Spacing>

    <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
      <Spacing size="XS" alignItems="center">
        <span
          style={{
            width: 8,
            height: 8,
            minWidth: 8,
            borderRadius: '50%',
            backgroundColor: seatZone.color || '#9B9B9B',
          }}
        />
        <Text size="S" color="grayLight" weight="regular">
          {seatZone.label || seatZone.zoneKey}
        </Text>
      </Spacing>
      <LocalizedCurrency
        price={seatPrice}
        currencyStyle="currency"
        size="S"
        color="grayLight"
        weight="regular"
      />
    </Spacing>
  </Spacing>
);

SeatBasedDetails.propTypes = {
  ticketData: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  showSeatFloor: PropTypes.bool,
  seatFloor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  seatZone: PropTypes.shape({
    color: PropTypes.string,
    zoneKey: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  seatPrice: PropTypes.number.isRequired,
};

/**
 * PassengerDetailsRow component.
 * Renders category, seat, deck, adjacent logo, overbooking and flat-fare copy for standard pricing.
 *
 * @param {object} props - ticketData, getCategoryTranslation, t, showSeat, showSeatFloor, seatFloor, isAdjacentSeat, overbooking, roundTrip, hidePassengerCategoryCopies, flatFareEnabled, flatFareContext, discountsTypes, discountType.
 * @param {object} props.ticketData - Ticket row data for display.
 * @param {Function} props.getCategoryTranslation - Maps adjacent flag and category to translated label.
 * @param {Function} props.t - i18n translation function.
 * @param {boolean} props.showSeat - Whether to show seat-related UI in the row.
 * @param {boolean} props.showSeatFloor - Whether to show deck badge.
 * @param {string|number} props.seatFloor - Deck label when shown.
 * @param {boolean} props.isAdjacentSeat - Whether adjacent seat logo is shown.
 * @param {boolean} props.overbooking - Overbooking state for badges.
 * @param {boolean} props.roundTrip - Round-trip flag for overbooking placement.
 * @param {boolean} props.hidePassengerCategoryCopies - Hides category and seat text when true.
 * @param {boolean} props.flatFareEnabled - Whether flat-fare reward copy may show.
 * @param {string} props.flatFareContext - i18n context for reward ticket label.
 * @param {object} props.discountsTypes - Map of discount type keys.
 * @param {string} props.discountType - Current ticket discount type key.
 * @returns {JSX.Element} PassengerDetailsRow component.
 */
const PassengerDetailsRow = ({
  ticketData,
  getCategoryTranslation,
  t,
  showSeat,
  showSeatFloor,
  seatFloor,
  isAdjacentSeat,
  overbooking,
  roundTrip,
  hidePassengerCategoryCopies,
  flatFareEnabled,
  flatFareContext,
  discountsTypes,
  discountType,
}) => (
  <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
    <Spacing alignItems="center">
      {!hidePassengerCategoryCopies && (
        <>
          <Text size="S" color="grayLight" weight="regular">
            {getCategoryTranslation(ticketData.isAdjacentSeat, ticketData.category)}
          </Text>

          <Text size="S" color="grayLight" weight="regular">
            {ticketData.showSeat && ticketData.seatHasNumber
              ? `${t('general:seat')} ${ticketData.seat}`
              : ''}
          </Text>
        </>
      )}
    </Spacing>

    <Spacing size="XS" alignItems="center">
      {(showSeat || showSeatFloor) && (
        <Spacing size="XS" alignItems="center">
          {showSeat && overbooking && roundTrip && <OverbookingBadge />}
          {showSeatFloor && (
            <Badge roundCorners type="gray">
              <Text elementType="span" size="S" weight="semibold">
                {t('general:deck')}
                <Text elementType="span" size="S" weight="bold">
                  {' '}
                  {seatFloor}
                </Text>
              </Text>
            </Badge>
          )}
        </Spacing>
      )}

      {isAdjacentSeat && <img src={adjacentLogo} alt="Placeholder" className="w-16 h-3" />}

      <Gap columnGap="S">
        {overbooking && !roundTrip ? <OverbookingBadge /> : null}

        {flatFareEnabled && discountsTypes[discountType] && (
          <Text elementType="span" size="S" color="loyaltyLight" weight="semibold" italic>
            {t('label.reward_ticket', { context: flatFareContext })}
          </Text>
        )}
      </Gap>
    </Spacing>
  </Spacing>
);

PassengerDetailsRow.propTypes = {
  ticketData: PropTypes.object.isRequired,
  getCategoryTranslation: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  showSeat: PropTypes.bool,
  showSeatFloor: PropTypes.bool,
  seatFloor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isAdjacentSeat: PropTypes.bool,
  overbooking: PropTypes.bool,
  roundTrip: PropTypes.bool,
  hidePassengerCategoryCopies: PropTypes.bool,
  flatFareEnabled: PropTypes.bool,
  flatFareContext: PropTypes.string,
  discountsTypes: PropTypes.object,
  discountType: PropTypes.string,
};

/**
 * PassengerDetails component.
 * Chooses between seat-based and standard passenger row layouts.
 *
 * @param {object} props - ticketData, getCategoryTranslation, t, showSeat, showSeatFloor, seatFloor, isAdjacentSeat, overbooking, roundTrip, hidePassengerCategoryCopies, flatFareEnabled, flatFareContext, discountsTypes, discountType, zoneMap, seatPrice, selectedSeatsForTrip.
 * @param {Object} props.ticketData - Ticket data object
 * @param {Function} props.getCategoryTranslation - Function to get category translation
 * @param {Function} props.t - Translation function
 * @param {boolean} props.showSeat - Whether to show seat
 * @param {boolean} props.showSeatFloor - Whether to show seat floor
 * @param {string|number} props.seatFloor - Seat floor
 * @param {boolean} props.isAdjacentSeat - Whether seat is adjacent
 * @param {boolean} props.overbooking - Whether has overbooking
 * @param {boolean} props.roundTrip - Whether is round trip
 * @param {boolean} props.hidePassengerCategoryCopies - Whether to hide passenger category copies
 * @param {boolean} props.flatFareEnabled - Whether flat fare is enabled
 * @param {string} props.flatFareContext - Flat fare context
 * @param {Object} props.discountsTypes - Discount types mapping
 * @param {string} props.discountType - Discount type
 * @param {Object} props.zoneMap - Zone map keyed by seat fare reference.
 * @param {number} props.seatPrice - Seat price for current ticket row.
 * @param {Array<Object>} props.selectedSeatsForTrip - Selected seats for the trip leg (zone resolution).
 * @returns {JSX.Element} - PassengerDetails component
 */
const PassengerDetails = ({
  ticketData,
  getCategoryTranslation,
  t,
  showSeat,
  showSeatFloor,
  seatFloor,
  isAdjacentSeat,
  overbooking,
  roundTrip,
  hidePassengerCategoryCopies,
  flatFareEnabled,
  flatFareContext,
  discountsTypes,
  discountType,
  zoneMap,
  seatPrice,
  selectedSeatsForTrip,
}) => {
  const useSeatBasedPricing = useShowPricesByGroup();
  const shouldUseSeatBasedPricing =
    useSeatBasedPricing &&
    (ticketData?.category === 'seat' || Object.keys(zoneMap || {}).length > 0);
  const seatZone = resolveSeatZoneForDetails(
    {
      ...ticketData,
      selectedSeatsForTrip,
    },
    zoneMap,
    seatPrice,
  );

  if (shouldUseSeatBasedPricing) {
    return (
      <SeatBasedDetails
        ticketData={ticketData}
        t={t}
        showSeatFloor={showSeatFloor}
        seatFloor={seatFloor}
        seatZone={seatZone}
        seatPrice={seatPrice}
      />
    );
  }

  return (
    <PassengerDetailsRow
      ticketData={ticketData}
      getCategoryTranslation={getCategoryTranslation}
      t={t}
      showSeat={showSeat}
      showSeatFloor={showSeatFloor}
      seatFloor={seatFloor}
      isAdjacentSeat={isAdjacentSeat}
      overbooking={overbooking}
      roundTrip={roundTrip}
      hidePassengerCategoryCopies={hidePassengerCategoryCopies}
      flatFareEnabled={flatFareEnabled}
      flatFareContext={flatFareContext}
      discountsTypes={discountsTypes}
      discountType={discountType}
    />
  );
};

PassengerDetails.propTypes = {
  ticketData: PropTypes.object.isRequired,
  getCategoryTranslation: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  showSeat: PropTypes.bool,
  showSeatFloor: PropTypes.bool,
  seatFloor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isAdjacentSeat: PropTypes.bool,
  overbooking: PropTypes.bool,
  roundTrip: PropTypes.bool,
  hidePassengerCategoryCopies: PropTypes.bool,
  flatFareEnabled: PropTypes.bool,
  flatFareContext: PropTypes.string,
  discountsTypes: PropTypes.object,
  discountType: PropTypes.string,
  zoneMap: PropTypes.object,
  seatPrice: PropTypes.number,
  selectedSeatsForTrip: PropTypes.arrayOf(PropTypes.object),
};

/**
 * Gets the price that should be shown for a selected seat before checkout.
 * @param {Object} props - Helper props.
 * @param {number} props.seatIndex - Selected seat position.
 * @param {number|string} props.seatPrice - Price stored in the seat layout.
 * @param {number|string} props.fallbackTotal - Reservation total fallback.
 * @param {number|string} props.fallbackPassengerTotal - Per-passenger fallback total.
 * @param {Object} props.fallbackPricing - Full pricing fallback used for discount availability.
 * @param {number} props.selectedCount - Number of selected seats for this trip.
 * @param {number} props.seatsCount - Backwards-compatible alias for selectedCount.
 * @param {boolean} props.useSeatBasedPricing - Whether seat prices should drive display.
 * @param {boolean} props.mixDiscountFaresByAvailability - Whether to mix discount and regular fares when availability is exceeded.
 * @returns {number|string} Price to display.
 */
export const getPreCheckoutSeatDisplayPrice = ({
  seatIndex = 0,
  seatPrice,
  fallbackTotal,
  fallbackPassengerTotal,
  fallbackPricing,
  selectedCount,
  seatsCount,
  useSeatBasedPricing,
  mixDiscountFaresByAvailability = false,
}) => {
  if (useSeatBasedPricing) return seatPrice;

  return getPassengerTotalBySelectionIndex({
    index: seatIndex,
    selectedCount: selectedCount ?? seatsCount,
    defaultPricing: fallbackPricing || { total: fallbackPassengerTotal ?? fallbackTotal },
    mixDiscountFaresByAvailability,
  });
};

/**
 * Gets the pricing object that matches the current trip leg context.
 * @param {Object} props - Helper props.
 * @param {Object} props.fragment - Purchase fragment.
 * @param {string} props.way - Purchase leg.
 * @param {boolean} props.roundTrip - Whether this is a round trip.
 * @returns {Object|undefined} Pricing for the fragment.
 */
const getPricingForFragment = ({ fragment, way, roundTrip }) => {
  if (!roundTrip) return fragment?.pricing;

  return way === 'returns' ? fragment?.roundTripPricing : fragment?.departureRoundTripPricing;
};

/**
 * Builds the per-passenger pricing object used before checkout.
 * @param {Object} props - Helper props.
 * @param {Object} props.reservation - Purchase reservation.
 * @param {string} props.way - Purchase leg.
 * @param {boolean} props.roundTrip - Whether this is a round trip.
 * @returns {Object} Per-passenger display pricing.
 */
export const getPreCheckoutPassengerDisplayPricing = ({ reservation, way, roundTrip }) => {
  const fragments = reservation?.fragments || [];
  const fragmentPricings = fragments
    .map((fragment) => getPricingForFragment({ fragment, way, roundTrip }))
    .filter(Boolean);

  if (!fragmentPricings.length) return reservation?.pricing || {};

  const total = fragmentPricings.reduce((sum, pricing) => sum + Number(pricing?.total || 0), 0);
  const totalBeforeDiscount = fragmentPricings.reduce(
    (sum, pricing) => sum + Number(pricing?.totalBeforeDiscount || 0),
    0,
  );
  const discountAvailabilities = fragmentPricings
    .map(getDiscountAvailability)
    .filter((availability) => availability != null);
  const providerDiscountsByName = fragmentPricings.reduce((discountsByName, pricing) => {
    (pricing?.providerDiscounts || []).forEach((discount) => {
      const current = discountsByName[discount.name] || {
        ...discount,
        total: 0,
        availability: null,
      };
      const discountAvailability = Number(discount?.availability);
      discountsByName[discount.name] = {
        ...current,
        ...discount,
        total: Number(current.total || 0) + Number(discount.total || 0),
        availability: Number.isFinite(discountAvailability)
          ? Math.min(current.availability ?? discountAvailability, discountAvailability)
          : current.availability,
      };
    });
    return discountsByName;
  }, {});

  return {
    ...reservation?.pricing,
    ...fragmentPricings[0],
    total: total || reservation?.pricing?.total,
    totalBeforeDiscount: totalBeforeDiscount || reservation?.pricing?.totalBeforeDiscount,
    discountAvailability: discountAvailabilities.length
      ? Math.min(...discountAvailabilities)
      : reservation?.pricing?.discountAvailability,
    providerDiscounts: Object.keys(providerDiscountsByName).length
      ? Object.values(providerDiscountsByName)
      : reservation?.pricing?.providerDiscounts,
  };
};

/**
 * Gets the per-passenger total used before checkout.
 * @param {Object} reservation - Purchase reservation.
 * @returns {number|string|undefined} Per-passenger total.
 */
export const getPreCheckoutPassengerDisplayTotal = (reservation) => {
  return getPreCheckoutPassengerDisplayPricing({ reservation })?.total;
};

/**
 * Displays a pre-checkout selected seat item.
 * @param {Object} props - Component props.
 * @param {Object} props.seat - Seat information.
 * @param {number} props.index - Seat index.
 * @param {number|string} props.price - Price to display.
 * @param {Function} props.t - Translation function.
 * @param {boolean} props.showSeatFareDescription - Whether to show seat fare description.
 * @returns {JSX.Element|null} Pre-checkout seat row.
 */
const PreCheckoutSeat = ({ seat, index, price, t, showSeatFareDescription }) => {
  const displayPrice = price ?? seat.price;

  if (displayPrice == null) return null;

  return (
    <>
      <div className="passenger-row">
        <div className="flex gap-2">
          <p className="text-sm font-semibold">{`${t('passengers:label.passenger')} ${
            index + 1
          }`}</p>
          <p className="text-sm font-bold">{seat.category === 'VIP' && seat.category}</p>
        </div>
      </div>
      <RowComponent
        label={t('label.price')}
        price={displayPrice}
        description={getFareDescription(seat.seatFareDescription, showSeatFareDescription)}
      />
    </>
  );
};

PreCheckoutSeat.propTypes = {
  seat: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  t: PropTypes.func.isRequired,
  showSeatFareDescription: PropTypes.bool,
};

const propTypes = {
  reservation: PropTypes.shape({
    tripSlug: PropTypes.string.isRequired,
    tickets: PropTypes.array.isRequired,
    pricing: PropTypes.shape({
      total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    }).isRequired,
    selectedSeats: PropTypes.array,
  }).isRequired,
  way: PropTypes.oneOf(['departs', 'returns']).isRequired,
  segment: PropTypes.string,
  titleLabel: PropTypes.string,
  currency: PropTypes.string,
  externalCouponCode: PropTypes.string,
  selectedSeatsBeforeCheckout: PropTypes.array,
  displayPricingBeforeCheckout: PropTypes.bool,
  selectedSeatsTotal: PropTypes.number,
  roundTrip: PropTypes.bool,
  isOpen: PropTypes.bool,
  hasCollapsibleTrigger: PropTypes.bool,
};

/**
 * PurchasePricingPassengerTrip component.
 * Displays the pricing information for each passenger or seat based on the configuration.
 *
 * @param {Object} props - Component props
 * @param {string} props.way - Trip leg (`departs` or `returns`).
 * @param {Object} props.reservation - Reservation object containing trip and tickets information
 * @param {string} props.segment - Trip segment.
 * @param {string} props.titleLabel - Title label for the section (e.g., "Detalle de ida")
 * @param {string} props.currency - Currency code
 * @param {string} props.externalCouponCode - External coupon code if available
 * @param {Array} props.selectedSeatsBeforeCheckout - Selected seats before checkout
 * @param {boolean} props.displayPricingBeforeCheckout - Whether to display pricing before checkout
 * @param {number} props.selectedSeatsTotal - Total amount for selected seats
 * @param {boolean} props.roundTrip - Whether the trip is a round trip
 * @param {boolean} props.isOpen - Whether the collapsible section is open
 * @param {boolean} props.hasCollapsibleTrigger - Whether to show collapsed passenger view
 * @returns {JSX.Element} - PurchasePricingPassengerTrip component
 */
const PurchasePricingPassengerTrip = ({
  reservation,
  way,
  segment,
  titleLabel,
  currency,
  externalCouponCode,
  selectedSeatsBeforeCheckout,
  displayPricingBeforeCheckout,
  selectedSeatsTotal,
  roundTrip,
  isOpen,
  hasCollapsibleTrigger,
}) => {
  const {
    PRECHECKOUT_SEATS,
    PURCHASE_PRICING_DETAILED_DISCOUNT,
    FLAT_FARE_ENABLED,
    FLAT_FARE_CONTEXT,
    HIDE_PASSENGER_CATEGORY_COPIES,
    SHOW_SEAT_FARE_DESCRIPTION,
    SHOW_ORIGINAL_PRICE_ON_CHECKOUT,
    SHOW_PASSENGER_PRICING_BREAKDOWN,
    CUSTOM_MANAGEMENT_COSTS_LABEL,
  } = useWhitelabelFeatures();
  const useSeatBasedPricing = useShowPricesByGroup();
  const mixDiscountFaresByAvailability = useMixDiscountFaresByAvailability();
  const urgencyDiscountUIEnabled = useUrgencyDiscountUIEnabled();

  const env = useWhitelabelEnvs();
  const breakdownValuesThatCanBeDisplayedAtCero =
    env.theme?.pricing?.breakdownValuesThatCanBeDisplayedAtCero || [];
  const purchase = usePurchase();
  const { t } = useTranslation(['purchase', 'trips', 'passengers', 'general']);
  const seatsBus = useSeatsBus();
  const { tickets, pricing, tripSlug, selectedSeats } = reservation;
  const discountsTypes = {
    single_trip_flat_fare_discount: 'single_trip_flat_fare_discount',
    departure_on_round_trip_flat_fare_discount: 'departure_on_round_trip_flat_fare_discount',
    single_on_round_trip_flat_fare_discount: 'single_on_round_trip_flat_fare_discount',
  };

  const overbooking = hasOversoldSeats(selectedSeats);
  const { zoneMap } = usePurchasePricingSeatZones({
    purchase,
    way,
    reservation,
    seatsBus,
    tripSlug,
    selectedSeatsBeforeCheckout,
  });

  const { total } = pricing;
  const passengerDisplayPricing = getPreCheckoutPassengerDisplayPricing({
    reservation,
    way,
    roundTrip,
  });
  const passengerDisplayTotal = passengerDisplayPricing?.total;
  const pricingTotal = tickets.length === 0 ? passengerDisplayTotal : total;

  // Determine if we should use selected seats for pricing based on feature flags
  const useSelectedSeats = useSeatBasedPricing && !tickets.length;

  /**
   * Get category translation
   * @param {boolean} isAdjacentSeat - Whether it's an adjacent seat
   * @param {string} category - Category name
   * @returns {string} - Translated category text
   */
  const getCategoryTranslation = (isAdjacentSeat, category) =>
    t(`general:category_${isAdjacentSeat ? 'adjacent_seat' : category}`, {
      context: env.brand,
    });

  // Final total price, computed via the shared usePricingTotal hook (also used by PurchasePricing).
  // Seat prices are only authoritative for seat-based pricing tenants/routes.
  const finalTotal = usePricingTotal({
    useSelectedSeats,
    selectedSeatsTotal,
    displayPricingBeforeCheckout,
    selectedSeatsBeforeCheckout,
    useSeatBasedPricing,
    defaultPricing: passengerDisplayPricing,
    mixDiscountFaresByAvailability,
    total: pricingTotal,
  });

  // Determine which tickets to display based on selected seats configuration
  const ticketsTarget = useSelectedSeats ? selectedSeatsBeforeCheckout : tickets;

  return (
    <>
      {titleLabel && <RowComponent label={titleLabel} price={finalTotal} isMain titleSize="M" />}
      {ticketsTarget.map((item, index) => {
        const ticketData = processTicketData(
          item,
          useSelectedSeats,
          t,
          PURCHASE_PRICING_DETAILED_DISCOUNT,
        );
        const seatPrice = (Number(ticketData?.amount) || 0) + (Number(ticketData?.taxes) || 0);
        const matchedPassenger = purchase.passengers?.find(
          (passenger) => passenger.id === ticketData.passengerId,
        );
        const lapChild = matchedPassenger?.meta?.lapChild;
        const hasLapChild = hasLapChildData(lapChild);
        const lapChildName = hasLapChild
          ? `${lapChild.firstName || ''} ${lapChild.lastName || ''}`.trim()
          : '';

        return (
          <div
            className={`flex flex-col transition-all duration-800 ease-in-out ${
              isOpen || hasCollapsibleTrigger ? 'gap-y-3' : 'gap-y-0'
            }`}
            key={`${tripSlug}-${ticketData.passengerId || index}`}
          >
            <PassengerTicketHeader
              passengerName={ticketData.passengerName}
              showSeat={ticketData.showSeat}
              showSeatFloor={ticketData.showSeatFloor}
              seatHasNumber={ticketData.seatHasNumber}
              seat={ticketData.seat}
              category={ticketData.category}
              seatFloor={ticketData.seatFloor}
              isAdjacentSeat={ticketData.isAdjacentSeat}
              discountType={item.discountType}
              hidePassengerCategoryCopies={HIDE_PASSENGER_CATEGORY_COPIES}
              flatFareEnabled={FLAT_FARE_ENABLED}
              flatFareContext={FLAT_FARE_CONTEXT}
              discountsTypes={discountsTypes}
              getCategoryTranslation={getCategoryTranslation}
              t={t}
              roundTrip={roundTrip}
              overbooking={overbooking}
              hasCollapsibleTrigger={hasCollapsibleTrigger}
              totalPrice={ticketData.total}
              isOpen={isOpen}
              discountAmount={ticketData.discountAmount}
              hasEarlyDiscount={ticketData.hasEarlyDiscount}
              discountLabels={ticketData.discountLabels}
              brand={env.brand}
              currency={currency}
              showDiscount={PURCHASE_PRICING_DETAILED_DISCOUNT}
              showDiscountUrgency={urgencyDiscountUIEnabled}
            />

            <div
              className={`flex flex-col gap-y-3 overflow-hidden transition-all duration-1000 ease-in-out ${
                isOpen || hasCollapsibleTrigger ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              {/* Passenger category and seat information - inside collapsible section */}
              <PassengerDetails
                ticketData={ticketData}
                getCategoryTranslation={getCategoryTranslation}
                t={t}
                zoneMap={zoneMap}
                seatPrice={seatPrice}
                selectedSeatsForTrip={
                  useSelectedSeats ? selectedSeatsBeforeCheckout : selectedSeats
                }
                showSeat={ticketData.showSeat}
                showSeatFloor={ticketData.showSeatFloor}
                seatHasNumber={ticketData.seatHasNumber}
                seat={ticketData.seat}
                category={ticketData.category}
                seatFloor={ticketData.seatFloor}
                isAdjacentSeat={ticketData.isAdjacentSeat}
                overbooking={overbooking}
                roundTrip={roundTrip}
                hidePassengerCategoryCopies={HIDE_PASSENGER_CATEGORY_COPIES}
                flatFareEnabled={FLAT_FARE_ENABLED}
                flatFareContext={FLAT_FARE_CONTEXT}
                discountsTypes={discountsTypes}
                discountType={item.discountType}
              />

              {hasLapChild && (
                <div className="flex flex-col gap-y-1">
                  <Text size="S" weight="semibold" color="grayStrong">
                    {lapChildName}
                  </Text>
                  <Spacing alignItems="center">
                    <Text size="S" color="grayLight" weight="regular">
                      {getCategoryTranslation(false, 'child_in_arms')}
                    </Text>
                  </Spacing>
                </div>
              )}

              <TicketPriceDetails
                tripSlug={tripSlug}
                passengerId={ticketData.passengerId}
                segment={segment}
                amount={ticketData.amount}
                discountAmount={ticketData.discountAmount}
                hasEarlyDiscount={ticketData.hasEarlyDiscount}
                discountLabels={ticketData.discountLabels}
                subtotal={ticketData.subtotal}
                taxes={ticketData.taxes}
                total={ticketData.total}
                toll={ticketData.toll}
                boardingTax={ticketData.boardingTax}
                managementCosts={ticketData.managementCosts}
                showBreakdown={SHOW_PASSENGER_PRICING_BREAKDOWN && !useSelectedSeats}
                canShowAtZero={breakdownValuesThatCanBeDisplayedAtCero}
                customManagementCostsLabel={CUSTOM_MANAGEMENT_COSTS_LABEL}
                seatFareDescription={ticketData.seatFareDescription}
                brand={env.brand}
                externalCouponCode={externalCouponCode}
                showSeatFareDescription={SHOW_SEAT_FARE_DESCRIPTION}
                showOriginalPriceOnCheckout={SHOW_ORIGINAL_PRICE_ON_CHECKOUT}
                seatPrice={seatPrice}
                showDiscountUrgency={urgencyDiscountUIEnabled}
                t={t}
              />
            </div>
          </div>
        );
      })}

      {PRECHECKOUT_SEATS &&
        tickets.length === 0 &&
        selectedSeatsBeforeCheckout.map((seat, index) => (
          <PreCheckoutSeat
            key={`seat-${seat.number || seat.id || `position-${index}`}`}
            seat={seat}
            index={index}
            price={getPreCheckoutSeatDisplayPrice({
              seatIndex: index,
              seatPrice: seat.price,
              fallbackTotal: total,
              fallbackPassengerTotal: passengerDisplayTotal,
              fallbackPricing: passengerDisplayPricing,
              selectedCount: selectedSeatsBeforeCheckout.length,
              useSeatBasedPricing,
              mixDiscountFaresByAvailability,
            })}
            t={t}
            showSeatFareDescription={useSeatBasedPricing && SHOW_SEAT_FARE_DESCRIPTION}
          />
        ))}
    </>
  );
};

PurchasePricingPassengerTrip.propTypes = propTypes;

export default PurchasePricingPassengerTrip;
